import { z } from 'zod';
import {
  requiredString,
  requiredUnion,
  optionalObject,
  optionalBoolean,
  optionalString,
  optionalNumber,
  optionalObjectArray,
  optionalArray,
  emptyRule,
} from './zod-helpers';
import { FeatureCode } from '../../../generated/graphql';

const sharedFieldsSchema = <T extends string>(v: T) => ({
  type: z.literal(v),
  enabled: z.literal(true),
  bgColor: requiredString('Background Color'),
  colorIndex: optionalNumber(),
});

export const disabledSectionTypePostfix = '-disabled';
// Function returning two schemas (enabled and disabled)
const optionalSection = <T extends z.ZodRawShape, TName extends string>(
  type: TName,
  v: T,
): [
  z.ZodObject<
    T & {
      type: z.ZodLiteral<TName>;
      enabled: z.ZodLiteral<true>;
      bgColor: z.ZodString;
      colorIndex: z.ZodOptional<z.ZodNumber>;
    },
    'strict'
  >, // Infer types properly with required fields
  z.ZodObject<{
    type: z.ZodLiteral<`${TName}${typeof disabledSectionTypePostfix}`>;
    enabled: z.ZodLiteral<false>;
  }>,
] => {
  // Schema for enabled section
  const enabledSchema = z
    .strictObject(sharedFieldsSchema(type))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .extend(v) as any;

  // Schema for disabled section
  const disabledSchema = z
    .object({
      type: z.literal(
        `${type}${disabledSectionTypePostfix}` as `${TName}${typeof disabledSectionTypePostfix}`,
      ),
      enabled: z.literal(false),
    })
    .passthrough();

  return [enabledSchema, disabledSchema];
};

const INTRO_TEXT_LABEL = 'Intro text';

const ctaSchema = optionalObject({
  enabled: optionalBoolean(),
  customize: optionalBoolean(),
  text: optionalString(),
  link: optionalString(),
});

export const introductionSectionSchema = z.object({
  ...sharedFieldsSchema('Introduction'),
  title: requiredString('Title', 60),
  subtitle: requiredString('Subtitle', 60),
  desc: emptyRule(
    z.string().max(500, {
      message: `${INTRO_TEXT_LABEL} is too long`,
    }),
  ),
  coverImage: requiredString('Cover image'),
  primaryColor: requiredString('Primary Color'),
  introVideo: optionalString(),
  logo: optionalString(),
  priceBefore: optionalNumber(),
  cta: ctaSchema,
});

export const emailCaptureSectionSchema = optionalSection('EmailCapture', {
  title: requiredString('Title', 60),
  description: requiredString('Description', 240),
  image: optionalString(),
  displayAsPopup: optionalBoolean(),
});

export const programStatsSectionSchema = optionalSection('ProgramStats', {});

export const reviewsSections = optionalSection('Reviews', {
  title: optionalString(),
  reviews: optionalObjectArray({
    name: requiredString('Name'),
    text: requiredString('Text'),
  }),
});

const textSectionSharedFields = {
  title: requiredString('Title', 60),
  desc: requiredString('Description'),
  bullets: optionalObjectArray({ text: requiredString('Bullet Text') }),
  cta: ctaSchema,
};

export const textSection1Schema = optionalSection('TextSection1', {
  ...textSectionSharedFields,
  coverImage: requiredString('Cover image'),
  introVideo: optionalString(),
});

export const textSection2Schema = optionalSection('TextSection2', {
  ...textSectionSharedFields,
  images: z
    .array(z.object({ url: z.string().nullable().default(null) }))
    .optional()
    .default([]),
  rotateImages: optionalBoolean(),
});

export const featuresSectionSchema = optionalSection('Features', {
  title: optionalString(),
  remindersCoverImage: optionalString(),
  streaksCoverImage: optionalString(),
  workoutsCoverImage: optionalString(),
  favoritesCoverImage: optionalString(),
  lifetimeCoverImage: optionalString(),
  streamCoverImage: optionalString(),
});

const featureTypes = [
  z.literal('reminders'),
  z.literal('streaks'),
  z.literal('tracking'),
  z.literal('favorites'),
  z.literal('videos'),
  z.literal('notes'),
  z.literal('stream'),
  z.literal('offline'),
  z.literal('playlist'),
  z.literal('nutrition'),
  z.literal('lifetime'),
] as const;

const otpFeaturesSectionSchema = optionalSection('OTPFeatures', {
  title: optionalString(),
  features: optionalObjectArray({
    type: requiredUnion(featureTypes, 'Feature Type'),
    name: requiredString('Feature Name'),
    image: requiredString('Feature Image'),
  }),
});

const featuresNewTypes = [
  ...featureTypes,
  z.literal('messages'),
  z.literal('community'),
] as const;
export const featuresNewSectionSchema = optionalSection('FeaturesNew', {
  title: optionalString(),
  features: z
    .array(
      z.object({
        type: requiredUnion(featuresNewTypes, 'Feature Type'),
        name: requiredString('Feature Name'),
        image: requiredString('Feature Image'),
      }),
    )
    .length(6, { message: 'Select 6 features' }),
});

export const workoutsSectionSchema = optionalSection('Workouts', {
  title: optionalString(),
  workoutIds: optionalArray(z.number()),
});

export const offerDetailsSectionSchema = optionalSection('OfferDetails', {
  headlineText: requiredString('Headline text', 60),
  programIds: optionalArray(z.number()),
  workoutsPreviewEnabled: optionalBoolean(),
  workoutIds: optionalArray(z.number()),
  extrasIds: optionalArray(z.string()),
  communityFeatures: optionalArray(z.nativeEnum(FeatureCode)),
});

export const bioSectionSchema = optionalSection('Bio', {
  title: requiredString('Title'),
  desc: requiredString('Description'),
  coverImage: requiredString('Cover image'),
  introVideo: optionalString(),
  swapSides: optionalBoolean(),
});

export const reviewsSectionSchema = optionalSection('Reviews', {
  title: optionalString(),
  reviews: optionalObjectArray({
    name: requiredString('Review Name'),
    text: requiredString('Reviews Text'),
  }),
});

export const testimonialsSectionSchema = optionalSection('Testimonials', {
  title: optionalString(),
  testimonials: optionalObjectArray({
    video: optionalString(),
    image: optionalString(),
    name: optionalString(),
    text: optionalString(),
  }),
});

export const faqsSectionSchema = optionalSection('FAQs', {
  title: optionalString(),
  faqs: optionalObjectArray({
    question: requiredString('Question'),
    answer: requiredString('Answer'),
  }),
});

const sectionSchema = z.discriminatedUnion('type', [
  introductionSectionSchema,
  ...emailCaptureSectionSchema,
  ...programStatsSectionSchema,
  ...reviewsSections,
  ...textSection1Schema,
  ...textSection2Schema,
  ...featuresSectionSchema,
  ...otpFeaturesSectionSchema,
  ...featuresNewSectionSchema,
  ...workoutsSectionSchema,
  ...offerDetailsSectionSchema,
  ...bioSectionSchema,
  ...testimonialsSectionSchema,
  ...faqsSectionSchema,
]);

export const OTPLandingPageSchema = z
  .object({
    sections: z.array(sectionSchema),
  })
  .superRefine((data, ctx) => {
    data.sections.forEach((section, i) => {
      if (
        section.type === 'OfferDetails' &&
        section.workoutsPreviewEnabled &&
        (section.workoutIds?.length ?? 0) === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sections', i, 'workoutIds'],
          message:
            'Select at least one workout or disable the workouts preview.',
        });
      }
    });
  });
export type ExtendWithDisabled<T> = T extends { type?: infer U extends string }
  ?
      | T
      | ({ type: `${U}${typeof disabledSectionTypePostfix}` } & {
          [K in Exclude<keyof T, 'type'>]?: T[K];
        })
  : never;

export type IntroductionSectionSchema = z.infer<
  typeof introductionSectionSchema
>;

export type EmailCaptureSectionSchema = z.infer<
  (typeof emailCaptureSectionSchema)[0]
>;
export type OTPFeaturesSectionSchema = z.infer<
  (typeof otpFeaturesSectionSchema)[0]
>;

export type ProgramStatsSectionSchema = z.infer<
  (typeof programStatsSectionSchema)[0]
>;

export type ReviewsSectionSchema = z.infer<(typeof reviewsSectionSchema)[0]>;

export type TextSection1Schema = z.infer<(typeof textSection1Schema)[0]>;

export type TextSection2Schema = z.infer<(typeof textSection2Schema)[0]>;

export type FeaturesSectionSchema = z.infer<(typeof featuresSectionSchema)[0]>;

export type FeaturesNewSectionSchema = z.infer<
  (typeof featuresNewSectionSchema)[0]
>;

export type WorkoutsSectionSchema = z.infer<(typeof workoutsSectionSchema)[0]>;

export type OfferDetailsSectionSchema = z.infer<
  (typeof offerDetailsSectionSchema)[0]
>;

export type BioSectionSchema = z.infer<(typeof bioSectionSchema)[0]>;

export type TestimonialsSectionSchema = z.infer<
  (typeof testimonialsSectionSchema)[0]
>;

export type FaqsSectionSchema = z.infer<(typeof faqsSectionSchema)[0]>;

export type SectionSchema = z.infer<typeof sectionSchema>;

export type SharedFieldsSchema = Required<
  Pick<IntroductionSectionSchema, 'enabled' | 'bgColor'>
> &
  Pick<IntroductionSectionSchema, 'colorIndex'>;
